import { FaChevronLeft, FaChevronRight, FaStar } from "react-icons/fa";
import CdesTooltip from "./CdesTooltip";

const PAGE_SIZE = 50;

const COLUMNS = [
    { key: "displayName", label: "Display name", width: 240 },
    { key: "fieldKey", label: "Key", width: 180 },
    { key: "category", label: "Category", width: 180 },
    { key: "dataType", label: "Data type", width: 120 },
    { key: "scope", label: "Scope", width: 120 },
    { key: "status", label: "Status", width: 110 },
    { key: "documentTypes", label: "Document types", width: 240 },
    { key: "actions", label: "Actions", width: 260 },
];

const tableWidth = COLUMNS.reduce((total, column) => total + column.width, 0);

const statusLabel = (status) => (status === "INACTIVE" ? "Inactive" : "Active");
const statusTone = (status) => (status === "INACTIVE" ? "muted" : "ok");
const scopeLabel = (scope) => (scope === "plan" ? "Plan" : "Client");
const tagSlug = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const FieldTable = ({
    query,
    fields,
    page,
    onPage,
    typeNames,
    reactivatingKey,
    onEdit,
    onDeactivate,
    onDelete,
    onReactivate,
}) => {
    const pageCount = Math.max(1, Math.ceil(fields.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageFields = fields.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const fieldCountLabel = `${fields.length} ${fields.length === 1 ? "field" : "fields"}`;

    let body;
    if (query.isLoading) {
        body = (
            <div className="loader-wrapper">
                <div className="loader" />
            </div>
        );
    } else if (query.isError) {
        body = (
            <div className="cdes-grid__state" role="alert">
                <p className="danger">{query.error.message || "Unable to load fields."}</p>
            </div>
        );
    } else if (fields.length === 0) {
        body = (
            <div className="cdes-grid__state">
                <p className="cdes-grid__state-title">No fields found</p>
                <p>No fields match these filters.</p>
            </div>
        );
    } else {
        body = (
            <div className="table-wrapper cdes-grid__scroll">
                <table className="cdes-grid__table cdes-field-table" style={{ width: "100%", minWidth: tableWidth }}>
                    <colgroup>
                        {COLUMNS.map((column) => (
                            <col key={column.key} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => (
                                <th key={column.key} title={column.label}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageFields.map((field) => {
                            const documentTypeNames = field.documentTypes.length
                                ? field.documentTypes
                                      .map((assignment) => typeNames.get(assignment.documentTypeKey) || assignment.documentTypeKey)
                                      .join(", ")
                                : "";
                            const busy = reactivatingKey === field.fieldKey;
                            return (
                                <tr key={field.fieldKey}>
                                    <td title={field.displayName}>
                                        <span className="cdes-field-name-cell">
                                            <span className="cdes-field-name">{field.displayName}</span>
                                            {field.universal ? (
                                                <CdesTooltip label="Universal field">
                                                    <span className="cdes-field-universal" tabIndex={0} aria-label="Universal field">
                                                        <FaStar aria-hidden />
                                                    </span>
                                                </CdesTooltip>
                                            ) : null}
                                        </span>
                                    </td>
                                    <td className="cdes-field-key" title={field.fieldKey}>
                                        {field.fieldKey}
                                    </td>
                                    <td title={field.category}>
                                        <span className={`cdes-field-tag cdes-field-tag--category cdes-field-tag--${tagSlug(field.category)}`}>
                                            {field.category}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`cdes-field-tag cdes-field-tag--type cdes-field-tag--${tagSlug(field.dataType)}`}>
                                            {field.dataType}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`cdes-field-tag cdes-field-tag--scope cdes-field-tag--${tagSlug(field.scope)}`}>
                                            {scopeLabel(field.scope)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`cdes-grid__status cdes-grid__status--${statusTone(field.status)}`}>
                                            {statusLabel(field.status)}
                                        </span>
                                    </td>
                                    <td className={documentTypeNames ? undefined : "is-empty"} title={documentTypeNames || undefined}>
                                        {documentTypeNames || "—"}
                                    </td>
                                    <td>
                                        <div className="cdes-client-actions">
                                            <button
                                                type="button"
                                                className="outline-btn"
                                                onClick={() => onEdit(field)}
                                                aria-label={`Edit ${field.displayName}`}
                                            >
                                                Edit
                                            </button>
                                            {field.status === "INACTIVE" ? (
                                                <button
                                                    type="button"
                                                    className="outline-btn"
                                                    onClick={() => onReactivate(field)}
                                                    disabled={busy}
                                                    aria-label={`Reactivate ${field.displayName}`}
                                                >
                                                    {busy ? "Reactivating…" : "Reactivate"}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="outline-btn"
                                                    onClick={() => onDeactivate(field)}
                                                    aria-label={`Deactivate ${field.displayName}`}
                                                >
                                                    Deactivate
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="outline-btn"
                                                onClick={() => onDelete(field)}
                                                aria-label={`Delete ${field.displayName}`}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <>
            {query.isSuccess && fields.length > 0 ? (
                <div className="cdes-grid__summary">
                    <span className="loading-with-label">
                        {pageCount > 1 ? `${pageFields.length} of ${fieldCountLabel} on this page` : fieldCountLabel}
                        {query.isFetching ? <span className="loader loader--inline" /> : null}
                    </span>
                    {pageCount > 1 ? (
                        <div className="cdes-grid__pager" aria-label="Field pages">
                            <button
                                type="button"
                                className="outline-btn cdes-grid__btn"
                                onClick={() => onPage(Math.max(0, safePage - 1))}
                                disabled={safePage === 0 || query.isFetching}
                                aria-label="Previous page"
                            >
                                <FaChevronLeft />
                            </button>
                            <span>
                                Page {safePage + 1} of {pageCount}
                            </span>
                            <button
                                type="button"
                                className="outline-btn cdes-grid__btn"
                                onClick={() => onPage(Math.min(pageCount - 1, safePage + 1))}
                                disabled={safePage >= pageCount - 1 || query.isFetching}
                                aria-label="Next page"
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {body}
        </>
    );
};

export { PAGE_SIZE };
export default FieldTable;
